import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { LogIn, FileText, Route, Wallet, Users } from 'lucide-react';
import cleanbidLogo from '../assets/cleanbid-logo.png';

// Login-only brand accent (per docs/CLEANBID_BRAND.md — scoped to this page).
const AQUA = '#71DED6';
const INK = '#10151D';

// Left-panel value props (replaces the previous KRO crow copy).
const HIGHLIGHTS = [
  { Icon: FileText, text: 'Send quotes and follow up with leads.' },
  { Icon: Route,    text: 'Plan better routes and keep your crew on schedule.' },
  { Icon: Wallet,   text: 'Track invoices, payments, and outstanding balances.' },
  { Icon: Users,    text: 'Keep customer details and job history together.' },
];

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const prev = document.title;
    document.title = 'Sign in | CleanBid.ai';
    return () => { document.title = prev; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const uid = cred.user.uid;

      // After successful login, decide between dashboard and signup
      try {
        if (db && uid) {
          const userRef = doc(db, 'users', uid);
          const snap = await getDoc(userRef);
          const data = snap.exists() ? snap.data() : null;

          if (data?.companyId) {
            navigate('/dashboard', { replace: true });
          } else {
            navigate('/signup', { replace: true });
          }
        } else {
          navigate('/dashboard', { replace: true });
        }
      } catch (navErr) {
        console.error('Login post-auth navigation failed:', navErr);
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to login. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">

      {/* ── LEFT PANEL ── */}
      <div
        className="hidden lg:flex lg:flex-col lg:justify-center lg:w-1/2 relative overflow-hidden"
        style={{ backgroundColor: '#111118' }}
      >
        {/* Radial green glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at 30% 55%, rgba(113,222,214,0.08) 0%, transparent 65%)',
            zIndex: 0,
          }}
        />

        {/* Official CleanBid logo + wordmark lockup — pinned top-left */}
        <div
          style={{
            position: 'absolute',
            top: '28px',
            left: '40px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            zIndex: 2,
          }}
        >
          <img
            src={cleanbidLogo}
            alt="CleanBid"
            style={{ height: '44px', width: 'auto', display: 'block' }}
            data-testid="login-brand-logo"
          />
          <span
            style={{ fontSize: '1.375rem', fontWeight: 700, letterSpacing: '-0.01em', color: '#F4F4F8' }}
            data-testid="login-brand-wordmark"
          >
            CleanBid<span style={{ color: AQUA }}>.ai</span>
          </span>
        </div>

        {/* CENTER: value proposition */}
        <div style={{ position: 'relative', zIndex: 1, padding: '80px 56px' }}>
          <p
            style={{
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: AQUA,
              marginBottom: '20px',
            }}
          >
            Built for home service businesses
          </p>

          <h1
            style={{
              fontSize: '2.75rem',
              fontWeight: 700,
              lineHeight: 1.1,
              color: '#F4F4F8',
              marginBottom: '20px',
              letterSpacing: '-0.02em',
            }}
          >
            Your next job.<br />Your whole business.
          </h1>

          <p
            style={{
              fontSize: '1.0625rem',
              fontWeight: 400,
              lineHeight: 1.6,
              color: '#B6B6CE',
              marginBottom: '40px',
              maxWidth: '30rem',
            }}
          >
            Keep your customers, quotes, scheduled work, and payments organized in one place.
          </p>

          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {HIGHLIGHTS.map(({ Icon, text }, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '38px',
                    height: '38px',
                    flexShrink: 0,
                    borderRadius: '10px',
                    backgroundColor: 'rgba(113,222,214,0.12)',
                    border: '1px solid rgba(113,222,214,0.28)',
                  }}
                >
                  <Icon size={18} color={AQUA} />
                </span>
                <span style={{ fontSize: '0.9375rem', fontWeight: 500, color: '#E4E4EE', lineHeight: 1.4 }}>
                  {text}
                </span>
              </li>
            ))}
          </ul>

          <p
            style={{
              fontSize: '1.125rem',
              fontWeight: 600,
              color: '#94E9E2',
              marginTop: '44px',
              letterSpacing: '-0.01em',
            }}
            data-testid="login-tagline"
          >
            Know your numbers. Know your business.
          </p>
        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div className="flex-1 flex items-center justify-center bg-[#0A0A0F] p-6 lg:p-12">
        <div className="w-full max-w-md">

          {/* Mobile wordmark — visible on mobile only, above sign-in headline */}
          <div className="lg:hidden mb-6 flex items-center justify-center gap-3">
            <img
              src={cleanbidLogo}
              alt="CleanBid"
              style={{ height: '38px', width: 'auto', display: 'block' }}
              data-testid="login-brand-logo-mobile"
            />
            <span
              style={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.01em', color: '#F4F4F8' }}
              data-testid="login-brand-wordmark-mobile"
            >
              CleanBid<span style={{ color: AQUA }}>.ai</span>
            </span>
          </div>

          {/* Form Header */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">
              Sign in to CleanBid.ai
            </h2>
            <p className="text-white/60">
              Welcome back. Let&apos;s get to work.
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/60 text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                data-testid="login-email-input"
                className="h-11 bg-white/5 border border-white/10 text-white placeholder:text-white/25 focus-visible:ring-0 focus-visible:border-[#71DED6]/60 rounded-lg"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/60 text-sm">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                data-testid="login-password-input"
                className="h-11 bg-white/5 border border-white/10 text-white placeholder:text-white/25 focus-visible:ring-0 focus-visible:border-[#71DED6]/60 rounded-lg"
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              data-testid="login-submit-button"
              className="w-full h-11 font-semibold rounded-lg shadow-none border-0 hover:opacity-90 transition-opacity"
              style={{ backgroundColor: AQUA, color: INK }}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-[#10151D] border-t-transparent rounded-full animate-spin" />
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <LogIn className="h-4 w-4" />
                  Sign In
                </span>
              )}
            </Button>
          </form>

          {/* Links */}
          <div className="mt-6 space-y-3 text-center text-sm">
            <Link
              to="/forgot-password"
              className="block font-medium hover:underline"
              style={{ color: AQUA }}
              data-testid="login-forgot-password-link"
            >
              Forgot Password?
            </Link>
            <p className="text-white/55">
              Don&apos;t have an account?{' '}
              <Link
                to="/signup"
                className="font-medium hover:underline"
                style={{ color: AQUA }}
                data-testid="login-create-account-link"
              >
                Create an Account
              </Link>
            </p>
          </div>

        </div>
      </div>

    </div>
  );
};

export default Login;
